/**
 * upload/DetailsStage.tsx — stage 1 of the upload overlay: the tool's catalogue metadata.
 *
 * Presentation only. It renders fields, reports changes upward, and shows the validation messages it
 * is handed; it decides nothing. The rules live in `validation.ts`, the state lives in the overlay,
 * and persistence lives in `draftStore.ts` — so this file can be read as "what the builder sees".
 *
 * Fields are the ones POST /api/tools requires, in the order the requirements describe them
 * (UP §5 group A: identity, name, what it does, classification, tags):
 *   name · description · problem solved · category · access · department · time saved ·
 *   dependencies · requirements (connector setup) · tags
 *
 * IDENTITY IS NOT AN INPUT. UP-F01 wants the builder's identity shown pre-filled and read-only from
 * their user record — and the backend derives the owner from the session and refuses to accept one
 * from the browser. So identity is displayed, never edited.
 *
 * The chips input, the field classes and the note styling are the EXISTING ones from Share.tsx and
 * shared.tsx, moved rather than redesigned, so the stage looks like the page it came from.
 *
 * ONE EXCEPTION TO "PRESENTATION ONLY": the tag field now offers the vocabulary the catalogue already
 * has (CAT-F12). It still decides nothing — the list arrives on `refData`, the matching is
 * `@trailblazer/shared`'s, and the server resolves and enforces as before. What this file owns is the
 * `tb-` look; the reskin's `design/TagsControl.tsx` shows the same list in the design language. Both
 * call the same three shared functions so the two skins cannot disagree about what a tag IS.
 */
import { useId, useState } from 'react';
import { describeTagSuggestion, matchTagSuggestions, normaliseTag } from '@trailblazer/shared';
import type { ToolRefData } from '../api';
import { LIMITS, type DetailsErrors, type DetailsField } from './validation';
import type { DraftDetails } from './draftStore';

/** How the overlay reaches individual controls to focus the first invalid one. */
export type FieldRefs = Partial<Record<DetailsField, HTMLElement | null>>;

export interface DetailsStageProps {
  details: DraftDetails;
  refData: ToolRefData;
  /** Validation messages to display. Empty until the builder has tried to continue (see showErrors). */
  errors: DetailsErrors;
  /**
   * Only true once Next has been pressed at least once. Before that the stage stays quiet — flagging
   * every field as invalid on a form the builder has not filled in yet is hostile, and UP-F44 asks for
   * clear states, not pre-emptive scolding.
   */
  showErrors: boolean;
  /** True when the pre-detect found connectors, which makes Requirements mandatory. */
  requirementsRequired: boolean;
  /** 'checking' shows a quiet progress note next to Requirements; the field is always visible. */
  connectorState: 'unknown' | 'checking' | 'has' | 'none' | 'error';
  builderName: string;
  builderDepartment: string | null;
  onChange: (patch: Partial<DraftDetails>) => void;
  /** Registers a control so the overlay can focus it. Called with null on unmount. */
  registerField: (field: DetailsField, el: HTMLElement | null) => void;
}

export function DetailsStage(props: DetailsStageProps) {
  const { details, refData, errors, showErrors, onChange, registerField } = props;

  /** Show a message only after the first Next press, so the stage does not pre-emptively scold. */
  const errorFor = (field: DetailsField): string | undefined =>
    showErrors ? errors[field] : undefined;

  /** The aria-describedby id for a field's error message. */
  const errId = (field: DetailsField) => `tb-err-${field}`;

  // ── Tags (CAT-F12) ──────────────────────────────────────────────────────────────────────────
  //
  // The field is CONTROLLED where it used to be uncontrolled: the suggestion list is a function of
  // what has been typed, so the typed text has to be state rather than only living in the DOM node.
  //
  // The matching, the CAT-F11 key and the hint text all come from `@trailblazer/shared` — the same
  // three functions the reskin's `design/TagsControl.tsx` calls. This skin owns its LOOK (the `tb-`
  // classes) and nothing else; if the two ever disagreed about which tag 'power_auto' means, one of
  // them would be disagreeing with the server, which resolves on that same normaliser.
  const [typedTag, setTypedTag] = useState('');
  const [tagsFocused, setTagsFocused] = useState(false);
  /** Keyboard position within the panel. -1 means "still in the input", where Enter commits the text. */
  const [activeTag, setActiveTag] = useState(-1);
  const tagListId = useId();

  const tagsAtCap = details.tags.length >= LIMITS.maxTags;
  const offeredTags = tagsAtCap ? [] : matchTagSuggestions(refData.tags, typedTag, details.tags);
  const tagPanelOpen = tagsFocused && offeredTags.length > 0;

  /**
   * Commit a raw tag label as a chip. These are the SAME UX guards the previous Share form applied —
   * trim, drop blanks, cap length, cap count, drop a duplicate. The backend remains the authority (it
   * normalises, de-dupes, creates-on-first-use and enforces CAT-F12).
   */
  function commitTag(raw: string): boolean {
    const label = raw.trim();
    if (label === '' || label.length > LIMITS.maxTagLength) return false;
    if (details.tags.length >= LIMITS.maxTags) return false;
    // Compared on the CAT-F11 key rather than `toLowerCase()`, which is what this line used to do:
    // 'power automate' and 'Power-Automate' are ONE tag to the server, so accepting both sent two
    // labels and produced one chip on the finished tool — a tag of the five silently lost.
    const key = normaliseTag(label);
    if (key === '' || details.tags.some((t) => normaliseTag(t) === key)) return false;
    onChange({ tags: [...details.tags, label] });
    return true;
  }

  /** Commit and reset the field — shared by Enter, blur and picking a suggestion. */
  function takeTag(label: string) {
    if (commitTag(label)) {
      setTypedTag('');
      setActiveTag(-1);
    }
  }

  function onTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && offeredTags.length > 0) {
      e.preventDefault();
      setActiveTag((i) => (i + 1) % offeredTags.length);
      return;
    }
    if (e.key === 'ArrowUp' && offeredTags.length > 0) {
      e.preventDefault();
      setActiveTag((i) => (i <= 0 ? offeredTags.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Escape' && tagPanelOpen) {
      // Stopped here: the overlay's own Escape handler closes the whole upload dialog, and a builder
      // dismissing a suggestion panel has not asked to abandon their draft.
      e.preventDefault();
      e.stopPropagation();
      setActiveTag(-1);
      setTagsFocused(false);
      return;
    }
    if (e.key === 'Enter' || e.key === ',') {
      // Enter must not submit the wizard form from inside the tag field.
      e.preventDefault();
      const chosen = activeTag >= 0 ? offeredTags[activeTag] : undefined;
      takeTag(chosen ? chosen.label : typedTag);
      return;
    }
    if (e.key === 'Backspace' && typedTag === '' && details.tags.length > 0) {
      onChange({ tags: details.tags.slice(0, -1) });
    }
  }

  return (
    <div>
      <p className="tb-muted" style={{ marginTop: 0 }}>
        Shared by <strong>{props.builderName}</strong>
        {props.builderDepartment ? ` · ${props.builderDepartment}` : ''} — taken from your user
        record.
      </p>

      <label className="tb-field">
        <span>
          Tool name <abbr title="required">*</abbr>
        </span>
        <input
          type="text"
          value={details.name}
          maxLength={LIMITS.nameMax}
          onChange={(e) => onChange({ name: e.target.value })}
          ref={(el) => registerField('name', el)}
          aria-invalid={errorFor('name') ? true : undefined}
          aria-describedby={errorFor('name') ? errId('name') : undefined}
        />
        {errorFor('name') ? (
          <span className="tb-err" id={errId('name')}>
            {errorFor('name')}
          </span>
        ) : (
          <span className="tb-field-hint">
            What a colleague would search for. {LIMITS.nameMin}–{LIMITS.nameMax} characters.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          What it does <abbr title="required">*</abbr>
        </span>
        <textarea
          value={details.description}
          maxLength={LIMITS.descriptionMax}
          onChange={(e) => onChange({ description: e.target.value })}
          ref={(el) => registerField('description', el)}
          aria-invalid={errorFor('description') ? true : undefined}
          aria-describedby={errorFor('description') ? errId('description') : undefined}
        />
        {errorFor('description') ? (
          <span className="tb-err" id={errId('description')}>
            {errorFor('description')}
          </span>
        ) : (
          <span className="tb-field-hint">
            {details.description.trim().length}/{LIMITS.descriptionMax} characters.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          The problem it solves <abbr title="required">*</abbr>
        </span>
        <textarea
          value={details.problemSolved}
          maxLength={LIMITS.problemMax}
          onChange={(e) => onChange({ problemSolved: e.target.value })}
          ref={(el) => registerField('problemSolved', el)}
          aria-invalid={errorFor('problemSolved') ? true : undefined}
          aria-describedby={errorFor('problemSolved') ? errId('problemSolved') : undefined}
        />
        {errorFor('problemSolved') ? (
          <span className="tb-err" id={errId('problemSolved')}>
            {errorFor('problemSolved')}
          </span>
        ) : (
          <span className="tb-field-hint">
            Why someone would reuse this — the manual work it removes.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Primary category <abbr title="required">*</abbr>
        </span>
        <select
          value={details.categoryId}
          onChange={(e) => onChange({ categoryId: Number(e.target.value) })}
          ref={(el) => registerField('categoryId', el)}
          aria-invalid={errorFor('categoryId') ? true : undefined}
          aria-describedby={errorFor('categoryId') ? errId('categoryId') : undefined}
        >
          {refData.categories.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        {errorFor('categoryId') ? (
          <span className="tb-err" id={errId('categoryId')}>
            {errorFor('categoryId')}
          </span>
        ) : (
          <span className="tb-field-hint">One category only. Product names go in tags.</span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Who may use it <abbr title="required">*</abbr>
        </span>
        <select
          value={details.accessId}
          onChange={(e) => onChange({ accessId: Number(e.target.value) })}
          ref={(el) => registerField('accessId', el)}
          aria-invalid={errorFor('accessId') ? true : undefined}
          aria-describedby={errorFor('accessId') ? errId('accessId') : undefined}
        >
          {refData.accessRequirements.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        {errorFor('accessId') ? (
          <span className="tb-err" id={errId('accessId')}>
            {errorFor('accessId')}
          </span>
        ) : (
          <span className="tb-field-hint">
            The role or licence someone needs before this will work for them.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Department it is for <abbr title="required">*</abbr>
        </span>
        <select
          value={details.departmentId}
          onChange={(e) => onChange({ departmentId: Number(e.target.value) })}
          ref={(el) => registerField('departmentId', el)}
          aria-invalid={errorFor('departmentId') ? true : undefined}
          aria-describedby={errorFor('departmentId') ? errId('departmentId') : undefined}
        >
          {refData.departments.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        {errorFor('departmentId') ? (
          <span className="tb-err" id={errId('departmentId')}>
            {errorFor('departmentId')}
          </span>
        ) : null}
      </label>

      <label className="tb-field">
        <span>
          Time saved each time it is used <abbr title="required">*</abbr>
        </span>
        <input
          type="number"
          min={LIMITS.timeSavedMin}
          max={LIMITS.timeSavedMax}
          step={1}
          value={details.estimatedTimeSaved}
          onChange={(e) => onChange({ estimatedTimeSaved: e.target.value })}
          ref={(el) => registerField('estimatedTimeSaved', el)}
          aria-invalid={errorFor('estimatedTimeSaved') ? true : undefined}
          aria-describedby={
            errorFor('estimatedTimeSaved') ? errId('estimatedTimeSaved') : undefined
          }
        />
        {errorFor('estimatedTimeSaved') ? (
          <span className="tb-err" id={errId('estimatedTimeSaved')}>
            {errorFor('estimatedTimeSaved')}
          </span>
        ) : (
          <span className="tb-field-hint">
            Minutes, {LIMITS.timeSavedMin}–{LIMITS.timeSavedMax}. Your best honest estimate.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Depends on <abbr title="required">*</abbr>
        </span>
        <input
          type="text"
          value={details.dependencies}
          onChange={(e) => onChange({ dependencies: e.target.value })}
          ref={(el) => registerField('dependencies', el)}
          aria-invalid={errorFor('dependencies') ? true : undefined}
          aria-describedby={errorFor('dependencies') ? errId('dependencies') : undefined}
        />
        {errorFor('dependencies') ? (
          <span className="tb-err" id={errId('dependencies')}>
            {errorFor('dependencies')}
          </span>
        ) : (
          <span className="tb-field-hint">
            Lists, mailboxes, licences or systems this needs. Write “None” if there are none.
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Connector setup{' '}
          {props.requirementsRequired ? (
            <abbr title="required">*</abbr>
          ) : (
            <span className="tb-muted">(optional)</span>
          )}
        </span>
        <textarea
          value={details.requirements}
          maxLength={LIMITS.requirementsMax}
          onChange={(e) => onChange({ requirements: e.target.value })}
          ref={(el) => registerField('requirements', el)}
          aria-invalid={errorFor('requirements') ? true : undefined}
          aria-describedby={errorFor('requirements') ? errId('requirements') : undefined}
        />
        {errorFor('requirements') ? (
          <span className="tb-err" id={errId('requirements')}>
            {errorFor('requirements')}
          </span>
        ) : (
          <span className="tb-field-hint">
            {props.connectorState === 'checking'
              ? 'Checking which connectors this flow uses…'
              : props.requirementsRequired
                ? 'This flow uses connectors. Explain which accounts or connections an adopter must set up, and why — their credentials are never copied with the flow.'
                : 'If an adopter needs to connect an account or system, say which and why.'}
          </span>
        )}
      </label>

      <label className="tb-field">
        <span>
          Tags <span className="tb-muted">(optional, up to {LIMITS.maxTags})</span>
        </span>
        {details.tags.length > 0 && (
          <ul className="tb-chips">
            {details.tags.map((tag, i) => (
              <li key={`${tag}-${i}`}>
                {tag}
                <button
                  type="button"
                  className="tb-chip-remove"
                  aria-label={`Remove tag ${tag}`}
                  onClick={() => onChange({ tags: details.tags.filter((_, j) => j !== i) })}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="tb-suggest-wrap">
          <input
            type="text"
            role="combobox"
            aria-expanded={tagPanelOpen}
            aria-controls={tagListId}
            aria-autocomplete="list"
            {...(tagPanelOpen && activeTag >= 0
              ? { 'aria-activedescendant': `${tagListId}-${activeTag}` }
              : {})}
            value={typedTag}
            maxLength={LIMITS.maxTagLength}
            placeholder={tagsAtCap ? 'Tag limit reached' : 'Type a tag, then Enter'}
            disabled={tagsAtCap}
            onChange={(e) => {
              setTypedTag(e.target.value);
              setActiveTag(-1);
            }}
            onFocus={() => setTagsFocused(true)}
            onKeyDown={onTagKeyDown}
            // Fold an uncommitted tag in on blur, so a builder who types and clicks away is not
            // silently ignored (the previous form did the same on submit).
            onBlur={() => {
              setTagsFocused(false);
              takeTag(typedTag);
            }}
          />
          {tagPanelOpen && (
            <ul
              id={tagListId}
              className="tb-suggest"
              role="listbox"
              aria-label="Tags already used across the catalogue"
            >
              {offeredTags.map((s, i) => (
                <li
                  key={s.normalised}
                  id={`${tagListId}-${i}`}
                  role="option"
                  aria-selected={i === activeTag}
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    // Without preventDefault the blur above fires FIRST and commits the half-typed
                    // text as its own tag, so picking 'Power Automate' after typing 'power' would
                    // quietly consume two of the five.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => takeTag(s.label)}
                  >
                    <span className="tb-suggest-label">{s.label}</span>
                    <span className="tb-suggest-meta">{describeTagSuggestion(s)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <span className="tb-field-hint">
          Products, systems or teams — e.g. Power Automate, Outlook, Finance.
        </span>
      </label>
    </div>
  );
}
